import { useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';
import { useGetSavedPaymentMethodQuery } from 'data/__generated__';

import { CopyValue } from 'components/CopyValue';
import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { PageActionsMenu, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';

import { SavedPaymentMethodDetails } from './components/PaymentInstrumentDetails';
import { PaymentsSection } from './components/PaymentsSection';

const ACTION_GROUPS: PageActionGroup[] = [
    {
        label: 'Navigation',
        items: [{ label: 'Retour aux paiements', icon: <ArrowLeft />, to: PAGES.payments.url }],
    },
];

// Une carte bancaire ou un titre-restaurant enregistré (collection `paymentMethods`). Crédits, subventions, rituels et
// formules ont leur propre fiche.
export const PaymentMethodDetails = () => {
    const { paymentMethodId = '' } = useParams<{ paymentMethodId: string }>();
    const { data, loading, error } = useGetSavedPaymentMethodQuery({ variables: { paymentMethodId } });

    if (loading && !data) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (error) {
        throw new Error('Une erreur est survenue lors de la récupération du moyen de paiement');
    }

    const savedPaymentMethod = data?.savedPaymentMethod;

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.paymentMethodDetails} />
                </HeaderTitle>
                <PageActionsMenu groups={ACTION_GROUPS} />
            </Header>
            <Content>
                <SectionContainer title="Informations" keepOpen>
                    <ColumnsSectionContainer numberOfColumns={3}>
                        <CopyValue label="Identifiant" value={paymentMethodId} />
                        {savedPaymentMethod ? (
                            <SavedPaymentMethodDetails savedPaymentMethod={savedPaymentMethod} />
                        ) : (
                            <Message>
                                Aucune carte ni aucun titre-restaurant enregistré ne porte cet identifiant
                            </Message>
                        )}
                    </ColumnsSectionContainer>
                </SectionContainer>
                <SectionContainer title="Paiements" isInitiallyOpen>
                    <SectionColumn>
                        <PaymentsSection
                            scope={{ paymentMethodIds: [paymentMethodId] }}
                            hiddenColumnIds={['userFullName']}
                            emptyMessage="Aucun paiement avec ce moyen de paiement"
                            dataTest="payment-method-payments"
                        />
                    </SectionColumn>
                </SectionContainer>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    padding: 15px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Message = styled.div`
    color: ${({ theme }) => theme.infoTextColor};
`;
